import { createStreams } from './core/rng';
import { startLoop } from './core/loop';
import { Engine } from './game/engine';
import { InputController } from './game/input';
import type { OfferId, RunMode, WeaponId } from './core/types';
import { Renderer } from './render/renderer';
import { Camera } from './render/camera';
import { Hud } from './render/hud';
import { AudioEngine } from './audio/audio';
import { Screens, type RankView } from './ui/screens';
import { FeedbackModal } from './ui/feedback';
import { WeaponPick } from './ui/weapon-pick';
import { BoardView } from './ui/board-view';
import { showHistory, showSlayerHall, wireNameForm } from './ui/profile';
import { contextFromEngine, type FeedbackContext } from './net/feedback';
import { fetchDailySeed, fetchPracticeSeed, type SeedBundle } from './net/api';
import { variantForDate } from './game/variants';
import { ensureIdentity, type ClientIdentity } from './net/identity';
import { submitScore, type RunSummary } from './net/score';
import { fetchRank } from './net/board';
import { copyInvite, copyToClipboard } from './net/clipboard';
import { AiTester } from './game/ai-tester';
import { drainEvents } from './game/event-drain';

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el;
}

function loadEntropy(): string {
  const KEY = 'dd_player_entropy';
  let v = localStorage.getItem(KEY);
  if (!v) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    v = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(KEY, v);
  }
  return v;
}

function main(): void {
  const canvas = $('game') as HTMLCanvasElement;
  const camera = new Camera();
  const renderer = new Renderer(canvas, camera);
  const audio = new AudioEngine();
  const input = new InputController($('joystick'), $('joystick-base'), $('joystick-knob'));
  const hud = new Hud({
    root: $('hud'),
    hpFill: $('hp-fill'),
    hpText: $('hp-text'),
    xpFill: $('xp-fill'),
    timer: $('timer'),
    level: $('level'),
    kills: $('kills'),
    gold: $('gold'),
    bossBar: $('boss-bar'),
    bossName: $('boss-name'),
    bossFill: $('boss-fill'),
  });
  const screens = new Screens({
    start: $('screen-start'),
    startStatus: $('start-status'),
    startRecovery: $('start-recovery'),
    levelup: $('screen-levelup'),
    cards: $('upgrade-cards'),
    dead: $('screen-dead'),
    deadCause: $('dead-cause'),
    deadTitle: $('dead-title'),
    deadTime: $('dead-time'),
    deadKills: $('dead-kills'),
    deadLevel: $('dead-level'),
    deadScore: $('dead-score'),
    deadReport: $('dead-report'),
    deadRank: $('dead-rank'),
    complete: $('screen-complete'),
    completeTitle: $('complete-title'),
    completeNote: $('complete-note'),
    completeTitleline: $('complete-titleline'),
    completeKills: $('complete-kills'),
    completeLevel: $('complete-level'),
    completeScore: $('complete-score'),
    completeReport: $('complete-report'),
    completeRank: $('complete-rank'),
    pause: $('screen-pause'),
  });
  const devSeed = (() => {
    if (!import.meta.env.DEV) return undefined;
    const raw = new URLSearchParams(location.search).get('seed');
    if (!raw) return undefined;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? (n >>> 0) : undefined;
  })();
  const entropy = loadEntropy();
  const weaponPick = new WeaponPick($('screen-weapon-pick'), $('weapon-cards'));
  let engine: Engine | null = null;
  let runMode: RunMode = 'daily';
  let pendingMode: RunMode = 'daily';
  let dailyBundle: SeedBundle | null = null;
  let practiceBundle: SeedBundle | null = null;
  let identity: ClientIdentity | null = null;
  let rankView: RankView | null = null;
  let levelupShown = false;
  let endAt: number | null = null;
  let endShown = false;
  let lastFrameNow = performance.now();
  let lastCtx: FeedbackContext | null = null;
  screens.showStart();

  const coarse = matchMedia('(pointer: coarse)').matches;
  const dprCap = coarse ? 1.5 : 2;
  const resize = (): void =>
    renderer.resize(innerWidth, innerHeight, Math.min(devicePixelRatio || 1, dprCap));
  window.addEventListener('resize', resize); resize();

  function doneKey(date: string, mode: 'daily' | 'race'): string {
    return `dd_done_${date}_${mode}`;
  }

  function refreshModeButtons(connected: boolean): void {
    const date = dailyBundle?.date ?? '';
    for (const [id, m] of [['btn-daily', 'daily'], ['btn-race', 'race']] as const) {
      ($(id) as HTMLButtonElement).disabled =
        !connected || !!date && localStorage.getItem(doneKey(date, m)) !== null;
    }
    ($('btn-practice') as HTMLButtonElement).disabled = !connected;
  }

  async function loadSeeds(): Promise<void> {
    screens.setStartStatus('正在连接今日战场…', true);
    refreshModeButtons(false);
    try {
      [dailyBundle, practiceBundle] = await Promise.all([
        fetchDailySeed(),
        fetchPracticeSeed(),
      ]);
      const dv = variantForDate(dailyBundle.date);
      const pv = variantForDate(practiceBundle.date);
      screens.setStartStatus(
        `今日 ${dailyBundle.date} · ${dv.label}：${dv.desc}\n` +
        `练习影子日 ${practiceBundle.date} · ${pv.label}`,
        true,
      );
      refreshModeButtons(true);
    } catch {
      screens.setStartStatus('连接失败，点此重试（需本地 API 服务运行）', false);
    }
  }
  $('start-status').addEventListener('click', () => {
    if (!dailyBundle) void loadSeeds();
  });

  function armRun(mode: RunMode): void {
    pendingMode = mode;
    screens.hideStart();
    weaponPick.show((w) => { weaponPick.hide(); startRun(pendingMode, w); });
  }

  function startRun(mode: RunMode, startWeapon: WeaponId = 'boomerang'): void {
    const bundle = mode === 'daily' ? dailyBundle
      : mode === 'race' ? dailyBundle : practiceBundle;
    if (!bundle) return;
    runMode = mode;
    audio.unlock();
    const seed = devSeed ?? bundle.seed;
    engine = new Engine(
      createStreams(seed, entropy), mode, seed, bundle.date, startWeapon,
    );
    if (import.meta.env.DEV) {
      (window as unknown as { __dd?: { engine: Engine } }).__dd = { engine };
    }
    input.reset(); input.enabled = true;
    levelupShown = false; endAt = null; endShown = false; rankView = null;
    for (const f of [screens.hideStart, screens.hideLevelUp, screens.hideDead,
      screens.hideComplete, screens.hidePause]) f.call(screens);
    hud.show();
    $('btn-pause').textContent = '⏸';
    $('btn-pause').classList.toggle('hidden', mode !== 'practice');
  }

  function backToMenu(): void {
    aiTester.stop($('ai-badge'));
    weaponPick.hide();
    for (const id of ['screen-feedback', 'screen-board',
      'screen-history', 'screen-slayers'])
      $(id).classList.add('hidden');
    engine = null; input.enabled = false; input.reset(); hud.hide();
    for (const f of [screens.hideDead, screens.hideComplete,
      screens.hideLevelUp, screens.hidePause]) f.call(screens);
    $('btn-pause').classList.add('hidden');
    $('btn-pause').textContent = '⏸';
    screens.showStart();
    refreshModeButtons(dailyBundle !== null);
  }

  function showLevelUp(): void {
    if (!engine) return;
    $('btn-reroll').textContent = `reroll（🪙 ${engine.variant.rerollCost}）`;
    screens.showLevelUp(engine, pick);
    levelupShown = true;
  }

  function pick(id: OfferId): void {
    if (!engine) return;
    screens.hideLevelUp(); levelupShown = false;
    engine.chooseUpgrade(id);
    if (engine.phase === 'levelup') showLevelUp();
    else input.enabled = true;
  }

  function reroll(): void {
    if (!engine) return;
    if (engine.reroll()) { screens.hideLevelUp(); showLevelUp(); }
  }

  function togglePause(): void {
    if (!engine || engine.phase === 'levelup') return;
    if (engine.phase === 'playing') {
      engine.pause(); input.enabled = false; input.reset(); screens.showPause();
    } else if (engine.phase === 'paused') {
      engine.resume(); input.enabled = true; screens.hidePause();
    }
    $('btn-pause').textContent = engine.phase === 'paused' ? '▶' : '⏸';
  }

  $('btn-daily').addEventListener('click', () => armRun('daily'));
  $('btn-race').addEventListener('click', () => armRun('race'));
  $('btn-practice').addEventListener('click', () => armRun('practice'));
  $('btn-weapon-back').addEventListener('click', () => {
    weaponPick.hide(); screens.showStart();
  });
  for (const id of ['btn-menu', 'btn-complete-back'])
    $(id).addEventListener('click', backToMenu);
  for (const id of ['btn-pause', 'btn-resume'])
    $(id).addEventListener('click', togglePause);
  $('btn-reroll').addEventListener('click', reroll);
  for (const id of ['dead-copy', 'complete-copy'])
    $(id).addEventListener('click', () =>
      void copyToClipboard(screens.reportText, $(id)));
  for (const id of ['dead-invite', 'complete-invite'])
    $(id).addEventListener('click', () =>
      copyInvite(screens.summary, $(id)));
  const boardView = new BoardView(
    $('board-list'), $('board-snapshot'), $('board-date-label'),
    $('board-mode-tabs'), $('board-region-tabs'),
    $('board-prev'), $('board-next'),
    () => identity?.token ?? '',
  );
  const open = (scr: string): void => $(scr).classList.remove('hidden');
  const openers: [string, string, () => void][] = [
    ['btn-board', 'screen-board', () => boardView.open()],
    ['btn-history', 'screen-history', () => {
      if (identity) void showHistory($('history-list'), identity);
    }],
    ['btn-slayers', 'screen-slayers',
      () => void showSlayerHall($('slayers-list'))],
  ];
  for (const [btn, scr, fn] of openers) {
    $(btn).addEventListener('click', () => { open(scr); fn(); });
  }
  for (const [btn, scr] of [
    ['btn-board-close', 'screen-board'],
    ['btn-history-close', 'screen-history'],
    ['btn-slayers-close', 'screen-slayers'],
  ] as const) {
    $(btn).addEventListener('click', () => $(scr).classList.add('hidden'));
  }
  const aiTester = new AiTester();
  aiTester.attach($('btn-ai'), $('ai-badge'), () => engine?.seed ?? 0,
    () => startRun('practice', 'boomerang'));
  const feedback = new FeedbackModal({
    screen: $('screen-feedback'), cats: $('fb-cats'),
    text: $('fb-text') as HTMLTextAreaElement,
    contact: $('fb-contact') as HTMLInputElement,
    ctxLine: $('fb-context'), status: $('fb-status'),
    submit: $('fb-submit') as HTMLButtonElement,
    cancel: $('fb-cancel') as HTMLButtonElement,
  }, () => identity, () => lastCtx);
  feedback.bind('btn-feedback-menu', 'dead-feedback', 'complete-feedback');

  window.addEventListener('keydown', (e) => {
    if (!engine) return;
    if (engine.phase === 'levelup') {
      const id = engine.offers[['1', '2', '3'].indexOf(e.key)] as OfferId | undefined;
      if (id) pick(id);
    } else if ((e.key === 'p' || e.key === 'Escape') && runMode === 'practice') { togglePause(); }
  });

  function toSummary(e: Engine): RunSummary {
    let evol = 0;
    for (const v of Object.values(e.build.evolved)) if (v) evol++;
    const bossKilled = e.boss1KilledAt !== null || e.boss2KilledAt !== null;
    const cause = e.phase === 'complete'
      ? (bossKilled ? 'VICTORY' : 'TIMEOUT')
      : e.death?.cause ?? 'ATTRITION';
    return {
      date: e.date,
      mode: runMode === 'race' ? 'race' : 'daily',
      score: e.scoreBreakdown?.total ?? 0,
      kills: e.kills,
      time: e.time,
      level: e.player.level,
      evolutions: evol,
      cause,
    };
  }

  async function settle(e: Engine): Promise<void> {
    if (runMode === 'practice') {
      rankView = { kind: 'practice' };
      return;
    }
    try {
      const summary = toSummary(e);
      if (identity) await submitScore(identity, summary);
      localStorage.setItem(doneKey(e.date, summary.mode), '1');
      if (!identity) {
        rankView = { kind: 'error', msg: 'offline' };
        return;
      }
      const r = await fetchRank(
        e.date, identity.token, runMode === 'race' ? 'race' : 'daily',
      );
      if ('pending' in r) rankView = { kind: 'pending' };
      else if ('unranked' in r) rankView = { kind: 'unranked' };
      else rankView = { kind: 'rank', r };
    } catch (err) {
      if (String((err as Error).message).includes('already submitted')) {
        rankView = { kind: 'done' };
      } else {
        rankView = { kind: 'error', msg: String(err) };
      }
    }
  }

  function handlePhases(now: number): void {
    if (!engine) return;
    if (engine.phase === 'levelup' && !levelupShown) {
      input.enabled = false;
      showLevelUp();
    }
    if (engine.phase === 'dead' || engine.phase === 'complete') {
      if (endAt === null) {
        endAt = now;
        lastCtx = contextFromEngine(engine);
        input.enabled = false; input.reset();
        void settle(engine);
      } else if (!endShown && now - endAt > 600) {
        endShown = true;
        hud.hide();
        const view = rankView ?? { kind: 'pending' } as RankView;
        if (engine.phase === 'dead') screens.showDead(engine, view);
        else screens.showComplete(engine, runMode, view);
      }
    }
  }

  startLoop({
    step() {
      if (engine) engine.step(aiTester.active ? aiTester.drive(engine) : input.sample());
    },
    render(alpha) {
      const now = performance.now();
      const frameDt = Math.min(0.1, (now - lastFrameNow) / 1000);
      lastFrameNow = now;
      handlePhases(now);
      if (engine) {
        drainEvents(engine, audio, camera);
        renderer.render(engine, alpha, now, frameDt);
        hud.update(engine);
      }
    },
    isFrozen() {
      return engine !== null && engine.freezeMs > 0;
    },
    consumeFreeze(dt) {
      if (engine) engine.freezeMs = Math.max(0, engine.freezeMs - dt * 1000);
    },
  });
  void loadSeeds();
  void ensureIdentity()
    .then((id) => {
      identity = id;
      if (id.recovery) {
        screens.setRecovery(`恢复码（建议抄一份存档）：${id.recovery}`);
      }
      wireNameForm(
        $('name-input') as HTMLInputElement, $('btn-name-set'),
        $('name-status'), () => identity,
        (n) => { if (identity) identity.name = n; },
      );
    })
    .catch(() => { /* 离线可玩，结算将提示未进榜 */ });
}

main();
